<script lang="ts" setup>
import Fuse from "fuse.js";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { vIntersectionObserver } from "@vueuse/components";

type UAvatarProps = {
    src: string | boolean;
    alt: string;
    text: string;
    size: "sm" | "2xs" | "xs" | "md" | "lg" | "xl";
    chipColor: string;
    chipPosition: string;
    chipText: string;
    ui?: Record<string, unknown> & {
        rounded?: string;
        background?: string;
    };
};

type UISelectMenuOption = Record<string, unknown> & {
    avatar?: Partial<UAvatarProps>;
    img?: {
        src: string;
        alt?: string;
    };
};

export interface UISelectMenuProps {
    modelValue: unknown;
    creatable?: boolean;
    noLabel?: boolean;
    optionAttribute?: string;
    searchAttributes?: string[];
    valueAttribute?: string | string[];
    descriptionAttribute?: string;
    searchable?: boolean | ((query: string) => Promise<UISelectMenuOption[]>);
    searchablePlaceholder?: string;
    options: (UISelectMenuOption | unknown)[];
    avatarUi?: object;
    uiMenu?: object;
    imgClass?: any;
    limit?: number;
    multiple?: boolean;
    disabled?: boolean;
    size?: "sm" | "2xs" | "xs" | "md" | "lg" | "xl";
    iconSize?:
    | "2xs"
    | "xs"
    | "sm"
    | "lg"
    | "xl"
    | "2xl"
    | "1x"
    | "2x"
    | "3x"
    | "4x"
    | "5x"
    | "6x"
    | "7x"
    | "8x"
    | "9x"
    | "10x";
    imgSize?: "sm" | "3xs" | "2xs" | "xs" | "md" | "lg" | "xl" | "2xl" | "3xl";
    placeholder?: string;
    none?: string;
    noneValue?: any;
}

const props = withDefaults(defineProps<UISelectMenuProps>(), {
    size: "sm",
    imgSize: "2xs",
    valueAttribute: undefined,
    avatarUi: () => ({}),
    searchable: false,
    iconSize: undefined,
    placeholder: undefined,
    searchAttributes: () => [
        "label",
        "key",
        "options.label",
        "options.value",
        "default_value",
    ],
    none: undefined,
    limit: 15,
    noneValue: undefined,
});

const emit = defineEmits<{
    (
        e: "update:modelValue",
        v: UISelectMenuOption | keyof UISelectMenuOption,
    ): void;
}>();

const query = defineModel<string>("query", { default: "" });

const filteredOptions = ref<UISelectMenuOption[]>([]);
const menu = ref();
const list = ref();
const loadedOptions = ref<UISelectMenuOption[]>([]);

const options = computed(() => {
    const items: any = [...(props.options ?? [])];

    if (props.none && "noneValue" in props) {
        items.splice(0, 0, {
            label: props.none,
            value: props.noneValue,
        });
    }

    if (Array.isArray(selected.value)) {
        for (const item of selected.value) {
            if (
                typeof item !== "object" ||
                Object.keys(item).length === 0 ||
                !("label" in item)
            )
                continue;

            const found = items.find((option: any) => {
                return (
                    option &&
                    typeof option === "object" &&
                    "label" in option &&
                    option.label === item.label
                );
            });

            if (!found) {
                items.push(item);
            }
        }
    }

    return items;
});

async function search(query: string) {
    let items: UISelectMenuOption[] = [];

    if (typeof props.searchable === "function") {
        items = await Promise.resolve(props.searchable(query));
    } else if (props.searchable && query.length > 2) {
        const fuse = new Fuse(options.value, {
            shouldSort: true,
            keys: props.searchAttributes,
            minMatchCharLength: 2,
            isCaseSensitive: false,
            threshold: 0.3,
            useExtendedSearch: true,
            ignoreLocation: true
        });

        items = fuse
            .search(query)
            .map(({ item }) => item as UISelectMenuOption);
    } else if (props.options?.length) {
        items = options.value as UISelectMenuOption[];
    }

    if (!Array.isArray(items)) {
        items = [];
    }

    filteredOptions.value = items;
    loadedOptions.value = [...items].splice(0, props.limit);
}

function onLoadMore() {
    if (loadedOptions.value?.length < filteredOptions.value?.length) {
        loadedOptions.value = [
            ...loadedOptions.value,
            ...[...filteredOptions.value].splice(
                loadedOptions.value.length ? loadedOptions.value.length : 0,
                props.limit,
            ),
        ];

        menu.value?.triggerExternalUpdate();
    }
}

function matchValueAttribute(
    option: UISelectMenuOption | UISelectMenuOption[],
) {
    const valueAttribute = Array.isArray(props.valueAttribute)
        ? props.valueAttribute
        : [props.valueAttribute];

    if (Array.isArray(option)) {
        return option
            .map(function (option: UISelectMenuOption) {
                for (const key of valueAttribute as unknown as keyof typeof option) {
                    if (key in option) return option[key];
                }

                if (option && props.creatable && "label" in option) {
                    return typeof option.label === "string"
                        ? option.label.trim()
                        : option.label;
                }

                return undefined;
            })
            .filter((value: any) => ![undefined, null].includes(value));
    }

    for (const key of valueAttribute as unknown as keyof typeof option) {
        if (key in option) return option[key];
    }

    if (option && props.creatable && "label" in option) {
        return typeof option.label === "string"
            ? option.label.trim()
            : option.label;
    }

    return undefined;
}

const stored = ref();

const selected = computed({
    get: () => {
        let value =
            props.modelValue &&
                ["number", "string"].includes(typeof props.modelValue)
                ? { label: props.modelValue, value: props.modelValue }
                : props.modelValue;

        if (!props.options) {
            return stored.value ? stored.value : value;
        }

        if (Array.isArray(props.modelValue)) {
            const opt = [...props.modelValue].map((modelValue) => {
                return (
                    (props.options.find(
                        (option) =>
                            props.modelValue === option ||
                            (props.valueAttribute &&
                                modelValue ===
                                matchValueAttribute(
                                    option as UISelectMenuOption,
                                )),
                    ) as UISelectMenuOption) ?? { label: modelValue }
                );
            });

            return opt.length === 1 ? opt[0] : opt;
        }

        const option = props.options.find(
            (option) =>
                props.modelValue === option ||
                (props.valueAttribute &&
                    props.modelValue ===
                    matchValueAttribute(option as UISelectMenuOption)),
        ) as UISelectMenuOption;

        value = option ? option : props.creatable ? value : undefined;

        if (!value && props.none) {
            return {
                label: props.placeholder ? props.placeholder : props.none,
            };
        }

        return value ?? undefined;
    },
    set: (option: UISelectMenuOption) => {
        if (!props.options) {
            stored.value = option;
        }

        if (option && props.none && option.label === props.none) {
            emit("update:modelValue", props.noneValue);
        } else {
            emit(
                "update:modelValue",
                (props.valueAttribute
                    ? matchValueAttribute(option as UISelectMenuOption)
                    : option) as UISelectMenuOption | any,
            );
        }
    },
});

onBeforeMount(() => {
    search(query.value);
});

const selectUiMenu = computed(() => {
    return useMerge(
        props.uiMenu ?? {},
        props.noLabel
            ? {
                trigger: "flex items-center",
                base: "focus:outline-none overflow-y-auto scroll-py-1",
            }
            : {},
    );
});

const debouncedSearch = useDebounce(search, 1000, {
    maxWait: 1000,
    trailing: true,
});

watch(query, debouncedSearch);
watch(
    () => props.options,
    () => {
        search(query.value);
        loadMoreIfEmpty();
    },
);

function onOpen() {
    list.value = menu.value?.$refs?.container?.querySelector("ul");
    loadMoreIfEmpty();
}

function loadMoreIfEmpty() {
    if (loadedOptions.value.length === 0) {
        onLoadMore();
    }
}

function onClose() {
    list.value = null;
    loadedOptions.value = [];
}

useInfiniteScroll(list, onLoadMore, {
    distance: 100,
    interval: 10,
});
</script>
<template>
    <UiBaseSelectMenu :disabled="disabled" :model-value="multiple && !Array.isArray(selected) ? [selected] : selected
        " @update:model-value="selected = $event" :creatable="creatable" :options="loadedOptions"
        :searchable="searchable ? () => loadedOptions : false" :size="size" ref="menu" @open="onOpen" :debounce="0"
        @close="onClose" :multiple="multiple" :search-attributes="searchAttributes" :ui-menu="selectUiMenu"
        select-class="ui-select" v-model:query="query">
        <template v-if="$slots.default" #default>
            <slot name="default" />
        </template>
        <template #label>
            <slot :selected="selected" name="label">
                <span v-if="noLabel">&nbsp;</span>
                <template v-else-if="
                    Array.isArray(selected) ? selected.length : selected
                ">
                    <template v-if="Array.isArray(selected)">{{ selected.length }} selected
                    </template>
                    <template v-else>
                        <FontAwesomeIcon v-if="selected.icon" :icon="selected.icon" :size="iconSize ?? size"
                            fixed-width />
                        <UAvatar v-else-if="selected.avatar" :size="imgSize ?? size" :ui="avatarUi" :class="imgClass"
                            v-bind="selected.avatar" />
                        <UIAvatar v-else-if="selected.img" :alt="selected.img.alt" :size="imgSize" :class="imgClass"
                            :src="selected.img.src" :ui="avatarUi" />
                        <template v-if="$slots.label">
                            <slot :icon-size="iconSize" :img-size="imgSize" :none="none" :none-value="noneValue"
                                :placeholder="placeholder" :selected="selected" :size="size"
                                :value-attribute="valueAttribute" name="label" />
                        </template>
                        <template v-else>
                            <div :class="{ hidden: noLabel }" v-if="
                                descriptionAttribute &&
                                selected &&
                                selected[descriptionAttribute]
                            ">
                                <div class="font-medium truncate">
                                    {{ (optionAttribute && selected[optionAttribute] ? selected[optionAttribute] :
                                        selected?.label) }}
                                </div>
                                <div>{{ selected[descriptionAttribute] }}</div>
                            </div>
                            <span v-else>{{ (optionAttribute && selected[optionAttribute] ? selected[optionAttribute] :
                                selected?.label) }}</span>
                        </template>
                    </template>
                </template>
                <template v-else-if="$slots.placeholder">
                    <slot name="placeholder" />
                </template>
                <template v-else-if="placeholder">
                    <span class="truncate text-neutral-500">{{
                        placeholder
                        }}</span>
                </template>
            </slot>
        </template>
        <template #start>
            <slot name="start" />
        </template>
        <template #end>
            <slot name="end" />
        </template>
        <template v-if="$slots.leading" #leading>
            <slot name="leading" />
        </template>
        <template #option="{ option, active, selected, index }">
            <slot
                :active="active"
                :selected="selected"
                :index="index"
                :icon-size="iconSize"
                :img-size="imgSize"
                :none="none"
                :none-value="noneValue"
                :option="option"
                :placeholder="placeholder"
                :size="size"
                :value-attribute="valueAttribute"
                name="option"
                v-if="$slots.option || (option && option.label) || (optionAttribute && option[optionAttribute]) || option.isString"
            >
                <FontAwesomeIcon v-if="option.icon" :icon="option.icon" :size="iconSize ?? size" fixed-width />
                <UAvatar v-else-if="option.avatar" :size="imgSize ?? size" :ui="avatarUi" :class="imgClass"
                    v-bind="option.avatar" class="h-full" />
                <UIAvatar v-else-if="option.img" :alt="option.img.alt" :size="imgSize" :class="imgClass"
                    :src="option.img.src" :ui="avatarUi" />
                <div v-if="descriptionAttribute && option[descriptionAttribute]">
                    <div class="font-medium">
                        {{
                            option && option.label
                                ? option.label
                                : optionAttribute && option[optionAttribute]
                                    ? option[optionAttribute]
                                    : option
                        }}
                    </div>
                    <div>{{ option[descriptionAttribute] }}</div>
                </div>
                <span v-else-if="option && option.label">
                    {{ option.label }}
                </span>
                <span v-else-if="optionAttribute && option[optionAttribute]">
                    {{ option[optionAttribute] }}
                </span>
                <span v-else-if="option.isString">
                    {{option.isString}}{{ option }}
                </span>
            </slot>
        </template>
    </UiBaseSelectMenu>
</template>
